/**
 * Bear / base / bull assumption packs — defaults calibrated to current FFC
 * (Aug 2026): ~8 paying logos, ~$80 MRR; realistic early-stage cash envelope.
 * Acquisition: Google / Reddit / LinkedIn + organic FB/IG posts — not Meta Ads.
 */

import { DEFAULT_RETENTION, normalizeRetention } from './cohorts';
import { DEFAULT_CHANNELS, PAID_BUDGET_MODE_LINK } from './channels';
import { growthForScenarioKey } from './growthOptions';
import {
  cloneDefaultMarketingPhases,
  DEFAULT_MARKETING_PHASES,
} from './marketingPhases';

export const SCENARIO_KEYS = ['bear', 'base', 'bull'];

export const SCENARIO_LABELS = {
  bear: 'Bear',
  base: 'Base',
  bull: 'Bull',
};

/**
 * Illustrative infra/tools breakout (edit to actuals). Total ≈ $120.
 * Plaid & Stripe are NOT here — they are variable COGS/logo.
 * SendGrid is included here (transactional email).
 */
export const FFC_OPEX_BREAKOUT = [
  { item: 'GCP Cloud Run + Cloud SQL (staging+prod)', amount: 60 },
  { item: 'Domains / DNS / TLS', amount: 10 },
  { item: 'SendGrid (transactional email)', amount: 15 },
  { item: 'GitLab / Cursor / 1Password / misc tools', amount: 35 },
];

export const FFC_DEFAULT_FIXED_OPEX_MO = FFC_OPEX_BREAKOUT.reduce((s, row) => s + row.amount, 0);

/** Fallback / M0–M6 learning phase when phases unset. */
export const FFC_DEFAULT_MARKETING_BUDGET_MO = DEFAULT_MARKETING_PHASES[0].budgetMo;

/** Default paid CAC — finance cold traffic is expensive (non-Meta channels). */
export const FFC_DEFAULT_PAID_CAC = 200;

/**
 * Blended Plaid + Stripe ≈ Economics portfolio variable COGS/paying logo at
 * current mix. Kept for legacy scenario compatibility; new projections use
 * the split Plaid/Stripe assumptions below.
 */
export const FFC_DEFAULT_VARIABLE_COGS_PER_LOGO = 3.3;
export const FFC_DEFAULT_STRIPE_COGS_PER_PAID_LOGO = 0.65;
export const FFC_DEFAULT_PLAID_COGS_PER_LINKED_LOGO =
  FFC_DEFAULT_VARIABLE_COGS_PER_LOGO - FFC_DEFAULT_STRIPE_COGS_PER_PAID_LOGO;

/**
 * Blend AI COGS/logo: ~10% Premium × ~avg turns × $0.04.
 * Free/Basic contribute $0 (gated).
 */
export const FFC_DEFAULT_AI_COGS_PER_LOGO = 0.12;

/**
 * Blend AI pack revenue/logo: ~4% power users buy ~1×$8 pack ≈ $0.32.
 * Included quota is not billed; overage packs only.
 */
export const FFC_DEFAULT_AI_PACK_REVENUE_PER_LOGO = 0.32;

export const FFC_DEFAULT_STARTING_CASH = 20000;

function phasesForPack(key) {
  if (key === 'bear') {
    return [
      { throughMonth: 6, budgetMo: 80, label: 'Learning (lean Google/Reddit + organic)' },
      { throughMonth: 18, budgetMo: 120, label: 'Proven (only if CAC clears)' },
      { throughMonth: 60, budgetMo: 180, label: 'Scale (slow raise)' },
    ];
  }
  if (key === 'bull') {
    return [
      { throughMonth: 6, budgetMo: 180, label: 'Learning (Google/Reddit/LinkedIn tests)' },
      { throughMonth: 18, budgetMo: 300, label: 'Proven (scale winners; no Meta Ads)' },
      { throughMonth: 60, budgetMo: 500, label: 'Scale (raise after payback)' },
    ];
  }
  return cloneDefaultMarketingPhases();
}

function baseFunnel(signupToPaidCumulative) {
  const signupToActivatePct = 60;
  // Paid logos only — bank-link is post-paid (Basic+/Plaid), not a conversion gate.
  const activateToPaidPct =
    Math.round((signupToPaidCumulative / (signupToActivatePct / 100)) * 1000) / 10;

  return {
    signupToActivatePct,
    activateToPaidPct: Math.min(100, activateToPaidPct),
    paidToBankLinkPct: 50,
    conversionLagMonths: 2,
    baseMonthlySignups: 10,
    signupGrowthPctMo: 5,
  };
}

function baseAssumptions(overrides = {}) {
  const marketingPhases = overrides.marketingPhases || cloneDefaultMarketingPhases();
  const marketingBudgetMo =
    overrides.marketingBudgetMo ?? marketingPhases[0]?.budgetMo ?? FFC_DEFAULT_MARKETING_BUDGET_MO;
  return {
    openingMrr: 80,
    openingLogos: 8,
    startingCash: FFC_DEFAULT_STARTING_CASH,
    fixedOpexMo: FFC_DEFAULT_FIXED_OPEX_MO,
    variableCogsPerLogo: FFC_DEFAULT_VARIABLE_COGS_PER_LOGO,
    plaidCogsPerLinkedLogo: FFC_DEFAULT_PLAID_COGS_PER_LINKED_LOGO,
    stripeCogsPerPaidLogo: FFC_DEFAULT_STRIPE_COGS_PER_PAID_LOGO,
    aiCogsPerLogo: FFC_DEFAULT_AI_COGS_PER_LOGO,
    aiPackRevenuePerLogo: FFC_DEFAULT_AI_PACK_REVENUE_PER_LOGO,
    horizonMonths: 24,
    arpuByTier: {
      basic: 9.99,
      premium: 24.99,
      enterprise: 99.99,
    },
    planMix: { basic: 0.9, premium: 0.1, enterprise: 0 },
    planMixGrowth: {
      upgradeBasicToPremiumPctMo: 0.5,
      upgradePremiumToEnterprisePctMo: 0,
    },
    expansionPctMo: 0.25,
    contractionPctMo: 0.5,
    ...overrides,
    marketingBudgetMo:
      overrides.marketingBudgetMo ??
      overrides.marketingPhases?.[0]?.budgetMo ??
      marketingBudgetMo,
    marketingPhases: overrides.marketingPhases || marketingPhases,
  };
}

function channelsWithMarketing(marketingBudgetMo, overrides = {}) {
  const budget = marketingBudgetMo ?? FFC_DEFAULT_MARKETING_BUDGET_MO;
  const paidOverrides = overrides.paid || {};
  return {
    ...DEFAULT_CHANNELS,
    ...overrides,
    organic: overrides.organic || {
      mode: 'growth',
      monthlySignups: 10,
      growthPctMo: 5,
    },
    paid: {
      enabled: paidOverrides.enabled !== false,
      budgetMode: paidOverrides.budgetMode || PAID_BUDGET_MODE_LINK,
      budgetMo: budget,
      cac: paidOverrides.cac ?? FFC_DEFAULT_PAID_CAC,
    },
    referral: overrides.referral || DEFAULT_CHANNELS.referral,
  };
}

/**
 * @param {'bear'|'base'|'bull'} key
 */
export function createScenarioPack(key) {
  const packs = {
    bear: (() => {
      const marketingPhases = phasesForPack('bear');
      const marketing = marketingPhases[0].budgetMo;
      return {
        assumptions: baseAssumptions({
          openingMrr: 72,
          openingLogos: 8,
          marketingBudgetMo: marketing,
          marketingPhases,
          expansionPctMo: 0,
          contractionPctMo: 1,
        }),
        funnel: baseFunnel(0.04),
        cohorts: { retention: normalizeRetention(DEFAULT_RETENTION.bear) },
        channels: channelsWithMarketing(marketing, {
          organic: { mode: 'growth', monthlySignups: 6, growthPctMo: 2 },
          paid: { cac: 250 },
          referral: { enabled: false, kFactor: 0.02, lagMonths: 1 },
        }),
        growth: growthForScenarioKey('bear'),
        events: [],
      };
    })(),
    base: (() => {
      const marketingPhases = phasesForPack('base');
      const marketing = marketingPhases[0].budgetMo;
      return {
        assumptions: baseAssumptions({
          marketingBudgetMo: marketing,
          marketingPhases,
        }),
        funnel: baseFunnel(0.06),
        cohorts: { retention: normalizeRetention(DEFAULT_RETENTION.base) },
        channels: channelsWithMarketing(marketing),
        growth: growthForScenarioKey('base'),
        events: [],
      };
    })(),
    bull: (() => {
      const marketingPhases = phasesForPack('bull');
      const marketing = marketingPhases[0].budgetMo;
      return {
        assumptions: baseAssumptions({
          openingMrr: 96,
          openingLogos: 10,
          marketingBudgetMo: marketing,
          marketingPhases,
          expansionPctMo: 0.75,
          contractionPctMo: 0.25,
          aiCogsPerLogo: 0.2,
          aiPackRevenuePerLogo: 0.5,
        }),
        funnel: baseFunnel(0.1),
        cohorts: { retention: normalizeRetention(DEFAULT_RETENTION.bull) },
        channels: channelsWithMarketing(marketing, {
          organic: { mode: 'growth', monthlySignups: 15, growthPctMo: 8 },
          paid: { cac: 150 },
          referral: { enabled: true, kFactor: 0.08, lagMonths: 1 },
        }),
        growth: growthForScenarioKey('bull'),
        events: [],
      };
    })(),
  };

  return JSON.parse(JSON.stringify(packs[key] || packs.base));
}

export function createAllScenarioPacks() {
  return Object.fromEntries(SCENARIO_KEYS.map((key) => [key, createScenarioPack(key)]));
}

export function getActivePack(scenario) {
  const key = scenario.activeScenario || 'base';
  return scenario.packs?.[key] || createScenarioPack(key);
}

export function resetScenarioPack(scenario, key) {
  return {
    ...scenario,
    packs: {
      ...scenario.packs,
      [key]: createScenarioPack(key),
    },
  };
}
